import {
  AlignItems,
  Body2,
  Bold,
  Box,
  Breakpoint,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading2,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import React from 'react'
import { getDisplayedStateTitle, isSkipOrder } from '../orderTileState'

type OrderTilePriceProps = {
  currentPrice: string | null
  discountBadge: OrderTileDiscountBadgeConfig | null
  originalPrice: string | null
  priceKind: string | null
}

type OrderTileDiscountBadgeConfig = {
  hideOnMobile: boolean
  hidePrices: boolean
  text: string
}

type OrderTileDiscountBadgeConfigParams = {
  currentPrice: string | null
  discountBadgeText: string | null
  orderState: DashboardExperienceOrderResponse['state']
  priceKind: string | null
}

const DESKTOP_PRICE_COLUMN_WIDTH = '160px'

const getOrderStateTitleColor = (order: DashboardExperienceOrderResponse) => {
  if (order.state === 'choose') {
    return Color.Warning_900
  }

  if (order.state === 'confirmed') {
    return Color.ColdGrey_600
  }

  if (isSkipOrder(order)) {
    return Color.Warning_900
  }

  return Color.Success_900
}

const getDiscountBadgeConfig = ({
  currentPrice,
  discountBadgeText,
  orderState,
  priceKind,
}: OrderTileDiscountBadgeConfigParams): OrderTileDiscountBadgeConfig | null => {
  if (priceKind === 'discount' && currentPrice) {
    return {
      hideOnMobile: false,
      hidePrices: true,
      text: currentPrice,
    }
  }

  if (orderState === 'edit' && priceKind === 'discounted' && discountBadgeText) {
    return {
      hideOnMobile: true,
      hidePrices: false,
      text: discountBadgeText,
    }
  }

  return null
}

const OrderTilePrice = ({
  currentPrice,
  discountBadge,
  originalPrice,
  priceKind,
}: OrderTilePriceProps) => {
  const shouldHidePrices = Boolean(discountBadge?.hidePrices)
  const shouldHideAllPrices = priceKind === null
  const shouldHideCurrentPrice = priceKind === 'price' && Boolean(originalPrice)
  const shouldShowOriginalPrice =
    !shouldHideAllPrices && !shouldHidePrices && Boolean(originalPrice)
  const shouldShowCurrentPrice =
    !shouldHidePrices && currentPrice && !shouldHideAllPrices && !shouldHideCurrentPrice

  if (!discountBadge && !shouldShowOriginalPrice && !shouldShowCurrentPrice) {
    return null
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      alignItems={AlignItems.FlexEnd}
    >
      {discountBadge ? (
        <Box
          minHeight="24px"
          paddingH={2}
          paddingV={0.5}
          bg={Color.Success_800}
          borderRadius={1.5}
          display={discountBadge.hideOnMobile ? ['none', 'none', 'flex'] : Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          data-testid="discount-badge"
        >
          <Text
            size={[2, 3]}
            textAlign={TextAlign.Center}
            color={Color.White}
            style={{ whiteSpace: 'nowrap' }}
          >
            {discountBadge.text}
          </Text>
        </Box>
      ) : null}
      {discountBadge && !discountBadge.hidePrices ? <Space size={1} direction="vertical" /> : null}
      {shouldShowOriginalPrice ? (
        <Text
          size={0}
          display={Display.Inline}
          textAlign={TextAlign.Right}
          color={Color.ColdGrey_600}
        >
          <s>{originalPrice}</s>
        </Text>
      ) : null}
      <Space size={0} direction="vertical" />
      {shouldShowCurrentPrice ? (
        <Text
          size={[4, 9]}
          display={Display.Inline}
          textAlign={TextAlign.Right}
          color={Color.ColdGrey_800}
        >
          <Bold>{currentPrice}</Bold>
        </Text>
      ) : null}
    </Box>
  )
}

const ManageDeliveryChevronButton = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

// Keeps the taller desktop price stack from increasing the left header height.
const DetachedDesktopPriceHeaderRow = withCitrusTheme(Box, () => ({
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    paddingRight: DESKTOP_PRICE_COLUMN_WIDTH,
    position: 'relative',
  },
}))

const DetachedDesktopPriceContainer = withCitrusTheme(Box, () => ({
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    position: 'absolute',
    right: 0,
    top: 0,
  },
}))

type MobileManageDeliveryToggleProps = {
  isManageDeliveryExpanded: boolean
  onToggle: () => void
}

const MobileManageDeliveryToggle = ({
  isManageDeliveryExpanded,
  onToggle,
}: MobileManageDeliveryToggleProps) => (
  <ManageDeliveryChevronButton
    role="button"
    tabIndex={0}
    aria-label="Toggle manage delivery details"
    aria-expanded={isManageDeliveryExpanded}
    aria-controls="manage-delivery-panel"
    display={['flex', 'flex', 'none']}
    alignItems={AlignItems.Center}
    justifyContent={JustifyContent.FlexEnd}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onToggle()
      }
    }}
  >
    <Icon
      name={isManageDeliveryExpanded ? 'chevron_up' : 'chevron_down'}
      variant={IconVariant.Actionable}
      size={8}
    />
  </ManageDeliveryChevronButton>
)

type OrderTileHeaderProps = {
  currentPrice: string | null
  discountBadgeText: string | null
  isManageDeliveryExpanded: boolean
  onManageDeliveryClick: () => void
  order: DashboardExperienceOrderResponse
  originalPrice: string | null
  priceKind: string | null
}

export const OrderTileHeader = ({
  currentPrice,
  discountBadgeText,
  isManageDeliveryExpanded,
  onManageDeliveryClick,
  order,
  originalPrice,
  priceKind,
}: OrderTileHeaderProps) => {
  const displayedStateTitle = getDisplayedStateTitle(order)
  const stateTitleColor = getOrderStateTitleColor(order)
  const summaryText = order.summary_text
  const hasSummaryText = Boolean(summaryText)
  const discountBadge = getDiscountBadgeConfig({
    currentPrice,
    discountBadgeText,
    orderState: order.state,
    priceKind,
  })
  const shouldDetachDesktopPrice = discountBadge?.hideOnMobile && !discountBadge.hidePrices
  const HeaderRow = shouldDetachDesktopPrice ? DetachedDesktopPriceHeaderRow : Box

  return (
    <Box
      width="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      alignItems={AlignItems.FlexStart}
    >
      <HeaderRow
        width="100%"
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        justifyContent={JustifyContent.SpaceBetween}
        alignItems={AlignItems.FlexStart}
      >
        <Box
          display={['flex', 'flex', 'none']}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.FlexStart}
          gap={1}
        >
          <Heading2 size={[3, 5]}>{order.title}</Heading2>
          <Body2 color={stateTitleColor} fontWeight={FontWeight.Bold}>
            {displayedStateTitle}
          </Body2>
        </Box>
        <Box
          display={['none', 'none', 'flex']}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.FlexStart}
          gap={1}
        >
          <Heading2 size={[3, 5]}>{order.title}</Heading2>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            alignItems={AlignItems.Center}
            gap={3}
          >
            <Body2 color={stateTitleColor} fontWeight={FontWeight.Bold}>
              {displayedStateTitle}
            </Body2>
            {hasSummaryText ? (
              <>
                <Body2 color={Color.ColdGrey_800}>|</Body2>
                <Body2 color={Color.ColdGrey_800}>{summaryText}</Body2>
              </>
            ) : null}
          </Box>
        </Box>
        {shouldDetachDesktopPrice ? (
          <DetachedDesktopPriceContainer>
            <OrderTilePrice
              currentPrice={currentPrice}
              discountBadge={discountBadge}
              originalPrice={originalPrice}
              priceKind={priceKind}
            />
          </DetachedDesktopPriceContainer>
        ) : (
          <OrderTilePrice
            currentPrice={currentPrice}
            discountBadge={discountBadge}
            originalPrice={originalPrice}
            priceKind={priceKind}
          />
        )}
      </HeaderRow>
      <Space size={1} direction="vertical" />
      <Box
        display={['flex', 'flex', 'none']}
        width="100%"
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
        gap={3}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          alignItems={AlignItems.Center}
          gap={3}
        >
          {hasSummaryText ? <Body2 color={Color.ColdGrey_800}>{summaryText}</Body2> : null}
        </Box>
        <MobileManageDeliveryToggle
          isManageDeliveryExpanded={isManageDeliveryExpanded}
          onToggle={onManageDeliveryClick}
        />
      </Box>
    </Box>
  )
}
